"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils/utils";

/**
 * Pre-procesa contenido del LLM antes de pasarlo a markdown:
 * - Reemplaza <br>, <br/>, <br /> con saltos de línea reales
 * - El LLM a veces emite HTML mezclado con markdown (sobre todo en tablas)
 *   que ReactMarkdown no renderiza por defecto
 */
function preprocessMarkdown(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

/**
 * Renderer markdown para mensajes del copiloto.
 * Soporta: bold, italic, code, lists, links, headings, tablas (gfm).
 * Estilo: tipografía compacta, hereda colores del contenedor.
 */
export function CopilotMarkdown({
  children,
  inverse = false,
}: {
  children: string;
  /** Si true (mensaje del user), invierte colores para fondo primario. */
  inverse?: boolean;
}) {
  const linkColor = inverse
    ? "text-primary-foreground underline underline-offset-2"
    : "text-primary underline underline-offset-2 hover:text-primary/80";
  const codeColor = inverse
    ? "bg-primary-foreground/20 text-primary-foreground"
    : "bg-muted text-foreground";

  return (
    <div className="prose-copilot space-y-1.5 break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="leading-relaxed [&:not(:last-child)]:mb-1.5">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn("font-medium", linkColor)}
            >
              {children}
            </a>
          ),
          // Código inline: renderizar como texto plano resaltado, sin aspecto técnico
          code: ({ children, ...props }) => {
            const isInline = !(props as { className?: string }).className;
            if (isInline) {
              return (
                <span
                  className={cn(
                    "rounded px-1 py-0.5 text-[12px] font-medium",
                    codeColor,
                  )}
                >
                  {children}
                </span>
              );
            }
            // code dentro de pre → ignorar, pre lo maneja
            return <>{children}</>;
          },
          // Bloques de código completos: no mostrar (contenido técnico no útil para el usuario final)
          pre: () => null,
          ul: ({ children }) => (
            <ul className="ml-4 list-disc space-y-0.5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="ml-4 list-decimal space-y-0.5">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          h1: ({ children }) => (
            <h1 className="text-[14px] font-semibold">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-[13px] font-semibold">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-[13px] font-semibold">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-[12px] font-semibold">{children}</h4>
          ),
          blockquote: ({ children }) => (
            <blockquote
              className={cn(
                "border-l-2 pl-2 italic",
                inverse ? "border-primary-foreground/40" : "border-primary/40",
              )}
            >
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr
              className={cn(
                "my-2 border-t",
                inverse
                  ? "border-primary-foreground/20"
                  : "border-border",
              )}
            />
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto rounded-md my-1">
              <table
                style={{ borderCollapse: "collapse", width: "100%", fontSize: "12px" }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th
              className={cn(
                "px-2.5 py-1.5 text-left text-[11px] font-semibold",
                inverse
                  ? "bg-white/10"
                  : "bg-foreground/[0.06]",
              )}
              style={{ border: "1px solid", borderColor: inverse ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)" }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              className="px-2.5 py-1"
              style={{ border: "1px solid", borderColor: inverse ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)" }}
            >
              {children}
            </td>
          ),
        }}
      >
        {preprocessMarkdown(children)}
      </ReactMarkdown>
    </div>
  );
}
